import { Button, ButtonGroup, Grid, TextField, Typography } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import moment from 'moment-timezone';
import React from 'react';

import { EAllowStatus } from '~/src/dash/api/console-api';
import { IPillItem } from '~/src/dash/components/pill-switcher';

export interface IDaypartingSchedule {
  Monday: string[][];
  Tuesday: string[][];
  Wednesday: string[][];
  Thursday: string[][];
  Friday: string[][];
  Saturday: string[][];
  Sunday: string[][];
}

export interface IDaypartingList {
  Enabled: boolean;
  Timezone: string;
  Schedule: IDaypartingSchedule;
  EnableViewerTimezone: boolean;
}

export type Day =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

const MomentIntToWeekdayStr: { [k: number]: string } = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

interface IActiveDays {
  Sunday: boolean;
  Monday: boolean;
  Tuesday: boolean;
  Wednesday: boolean;
  Thursday: boolean;
  Friday: boolean;
  Saturday: boolean;
  [key: string]: boolean;
}

export interface IDaypartingInterval {
  ActiveDays: IActiveDays;
  StartTime: moment.Moment;
  EndTime: moment.Moment;
  Error: string;
}

export interface ISchedule {
  Monday: any;
  Tuesday: any;
  Wednesday: any;
  Thursday: any;
  Friday: any;
  Saturday: any;
  Sunday: any;
  [key: string]: any;
}

export interface IPeriod {
  start: moment.Moment;
  end: moment.Moment;
}

export const scheduleMoments = {
  Monday: [] as IPeriod[],
  Tuesday: [] as IPeriod[],
  Wednesday: [] as IPeriod[],
  Thursday: [] as IPeriod[],
  Friday: [] as IPeriod[],
  Saturday: [] as IPeriod[],
  Sunday: [] as IPeriod[],
};

export const scheduleStrings = {
  Monday: [] as string[],
  Tuesday: [] as string[],
  Wednesday: [] as string[],
  Thursday: [] as string[],
  Friday: [] as string[],
  Saturday: [] as string[],
  Sunday: [] as string[],
};

// Timezone type enum
export const DaypartingTimezonesTypeUnknownString: string = 'Unknown';
export const DaypartingTimezonesTypeStandardString: string = 'Line Item Timezone';
export const DaypartingTimezonesTypeBidderString: string = 'Bidder Timezone';
export const DaypartingTimezonesTypeViewerString: string = 'Viewer Timezone';

export const DAYPARTING_TIMEZONES_PILLS: IPillItem[] = [
  {
    text: DaypartingTimezonesTypeStandardString,
    icon: {
      active: '/assets/shared/svg/globeActive.svg',
      inactive: '/assets/shared/svg/globeInactive.svg',
    },
  },
  {
    text: DaypartingTimezonesTypeViewerString,
    icon: {
      active: '/assets/shared/svg/eyeActive.svg',
      inactive: '/assets/shared/svg/eyeInactive.svg',
    },
  },
];

export const DAYPARTING_TIMEZONES_BIDDER_PILLS: IPillItem[] = [
  {
    text: DaypartingTimezonesTypeBidderString,
    icon: {
      active: '/assets/shared/svg/globeActive.svg',
      inactive: '/assets/shared/svg/globeInactive.svg',
    },
  },
  {
    text: DaypartingTimezonesTypeViewerString,
    icon: {
      active: '/assets/shared/svg/eyeActive.svg',
      inactive: '/assets/shared/svg/eyeInactive.svg',
    },
  },
];

export function daypartingTimezonesTypeToString(enableViewerTimezone: boolean): string {
  switch (enableViewerTimezone) {
    case false:
      return DaypartingTimezonesTypeStandardString;
    case true:
      return DaypartingTimezonesTypeViewerString;
    default:
      break;
  }
  return DaypartingTimezonesTypeUnknownString;
}

export function daypartingTimezonesBidderTypeToString(enableViewerTimezone: boolean): string {
  return enableViewerTimezone
    ? DaypartingTimezonesTypeViewerString
    : DaypartingTimezonesTypeBidderString;
}

export function daypartingTimezonesTypeToBoolean(daypartingTimezonesType: string): boolean {
  if (daypartingTimezonesType === DaypartingTimezonesTypeViewerString) {
    return true;
  }
  return false;
}

interface IProps {
  DaypartingIntervals: IDaypartingInterval[];
  handleDateChange: (index: number, time: string, dateType: 'start' | 'end') => void;
  handleDayClick: (day: string, index: number) => void;
  handleAddDaypartingPeriod: () => void;
  handleDeleteDaypartingPeriod: (index: number) => void;
  classes: { [key: string]: string };
}

export const handleDaypartingDateChange = (
  index: number,
  time: string,
  dateType: 'start' | 'end',
  daypartingIntervals: IDaypartingInterval[],
): IDaypartingInterval[] => {
  // convert time string from text field to moment
  const newTime = moment(time, 'HH:mm');
  const DaypartingIntervals = cloneDeep(daypartingIntervals);
  if (dateType === 'start') {
    if (DaypartingIntervals[index].EndTime.isBefore(newTime)) {
      DaypartingIntervals[index].Error = 'Start time must be before end time';
    } else {
      DaypartingIntervals[index].StartTime = newTime;
      DaypartingIntervals[index].Error = '';
    }
  } else {
    if (DaypartingIntervals[index].StartTime.isAfter(newTime)) {
      DaypartingIntervals[index].Error = 'End time must be after start time';
    } else {
      DaypartingIntervals[index].EndTime = newTime;
      DaypartingIntervals[index].Error = '';
    }
  }

  return DaypartingIntervals;
};

export const handleDaypartingDayClick = (
  day: Day,
  idx: number,
  daypartingIntervals: IDaypartingInterval[],
): IDaypartingInterval[] => {
  const DaypartingIntervals = cloneDeep(daypartingIntervals);
  DaypartingIntervals[idx].ActiveDays[day] = !DaypartingIntervals[idx].ActiveDays[day];
  return DaypartingIntervals;
};

export const handleDeleteDaypartingPeriod = (
  index: number,
  daypartingIntervals: IDaypartingInterval[],
): IDaypartingInterval[] => {
  const DaypartingIntervals = cloneDeep(daypartingIntervals);
  DaypartingIntervals.splice(index, 1);
  return DaypartingIntervals;
};

export const handleAddDaypartingPeriod = (
  daypartingIntervals: IDaypartingInterval[],
): IDaypartingInterval[] => {
  const DaypartingIntervals = cloneDeep(daypartingIntervals);
  const newDaypartingInterval = {
    ActiveDays: {
      Sunday: false,
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
    },
    StartTime: moment().startOf('day'),
    EndTime: moment().startOf('day').add(12, 'hours'),
    Error: '',
  };
  DaypartingIntervals.push(newDaypartingInterval);
  return DaypartingIntervals;
};

// takes the intervals and converts into a stringified json object to be sent via API
// daypartingIsAllowed should only potentially be true when checkForecast is run since we may want to invert the dayparting
export const parseDaypartingIntervals = (
  DaypartingIntervals: IDaypartingInterval[],
  isDaypartingActive: boolean,
  Timezone: string,
  daypartingIsAllowed: boolean,
  enableViewerTimezone?: boolean,
): string => {
  const schedule = cloneDeep(scheduleMoments);
  const DaypartingObj: IDaypartingList = {
    Enabled: isDaypartingActive,
    Timezone,
    Schedule: {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    },
    EnableViewerTimezone: enableViewerTimezone || false,
  };
  DaypartingIntervals.forEach((interval) => {
    Object.keys(interval.ActiveDays).forEach((day: Day) => {
      if (interval.ActiveDays[day]) {
        schedule[day].push({
          start: cloneDeep(interval.StartTime),
          end: cloneDeep(interval.EndTime),
        });
      }
    });
  });
  // iterate over the days of the week
  for (const key of Object.keys(schedule) as Day[]) {
    let dayOfWeek: Day;
    if (
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(key)
    ) {
      dayOfWeek = key;
    } else {
      return '';
    }
    let periodsForDay = schedule[dayOfWeek];
    periodsForDay = collapsePeriods(periodsForDay);
    // if dayparting is not allowed, we need to invert all dayparting periods
    if (!daypartingIsAllowed) {
      if (periodsForDay.length === 0) {
        // if there are no dayparting periods for this day, the whole day is allowed
        periodsForDay.push({ start: moment().startOf('day'), end: moment().endOf('day') });
      } else {
        // for each period, find the time inbetween the start of the day or end of the last period and create a period
        let currentStart = cloneDeep(periodsForDay[0].start).startOf('day');
        const newPeriodsForDay = [] as IPeriod[];
        for (let i = 0; i < periodsForDay.length; i++) {
          const period = periodsForDay[i];
          // if the current start is before period start, add a new period
          if (currentStart.isBefore(period.start)) {
            newPeriodsForDay.push({
              start: cloneDeep(currentStart),
              end: cloneDeep(period.start).add(-1, 'minutes'),
            });
          }
          // move the current start to the end of the period
          currentStart = cloneDeep(period.end).add(1, 'minutes');
          // if this is the last period and period end is not end of day, create a period for the end => end of day
          if (
            i === periodsForDay.length - 1 &&
            !(period.end.hour() === 23 && period.end.minutes() === 59)
          ) {
            newPeriodsForDay.push({ start: cloneDeep(period.end), end: moment().endOf('day') });
          }
        }
        periodsForDay = newPeriodsForDay;
      }
    }
    periodsForDay.forEach((period: IPeriod) => {
      DaypartingObj.Schedule[dayOfWeek].push([
        period.start.format('HH:mm'),
        period.end.format('HH:mm'),
      ]);
    });
  }
  return JSON.stringify(DaypartingObj);
};

// takes the intervals and invokes parseDaypartingIntervals to convert back into object to be displayed on sidebar for bidder cloning
// and bidder bulk updates
export const parseDaypartingGenerateDisplay = (
  DaypartingIntervals: IDaypartingInterval[],
  isDaypartingActive: boolean,
  Timezone: string,
  daypartingIsAllowed: boolean,
): any => {
  const DaypartingObj: IDaypartingList = JSON.parse(
    parseDaypartingIntervals(
      DaypartingIntervals,
      isDaypartingActive,
      Timezone,
      daypartingIsAllowed,
    ),
  );
  const display = [] as any;
  display.push(`Timezone: ${DaypartingObj.Timezone}`);
  for (const day in DaypartingObj.Schedule) {
    if (DaypartingObj.Schedule.hasOwnProperty(day)) {
      const timePeriod = DaypartingObj.Schedule[day as Day];
      if (timePeriod.length > 0) {
        timePeriod.forEach((time: any) => {
          display.push(`${day}: ${time.join('-')}`);
        });
      } else {
        display.push(`${day}`);
      }
    }
  }
  return display;
};

const periodsOverlapping = (a: IPeriod, b: IPeriod) => {
  if (
    b.start.isBetween(a.start, a.end, 'minutes', '[]') ||
    b.end.isBetween(a.start, a.end, 'minutes', '[]')
  ) {
    return true;
  } else {
    return false;
  }
};

// collapse overlapping periods by looping over them and seeing if a period has a start or end
// that is in the middle of the other period, if so, we can combine those periods and keep looping
export const collapsePeriods = (periods: IPeriod[]) => {
  periods.sort((a: IPeriod, b: IPeriod) => (a.start.isBefore(b.start) ? -1 : 1));
  let overlapping = true;
  while (overlapping && periods.length > 1) {
    const collapsedPeriods = [];
    overlapping = false;
    let i = 0;
    while (i < periods.length - 1) {
      const firstPeriod = periods[i];
      const secondPeriod = periods[i + 1];

      if (periodsOverlapping(firstPeriod, secondPeriod)) {
        if (secondPeriod.end.isAfter(firstPeriod.end)) {
          collapsedPeriods.push({ start: firstPeriod.start, end: secondPeriod.end });
        } else {
          collapsedPeriods.push(firstPeriod);
        }
        i = i + 2;
        overlapping = true;
        continue;
      }
      collapsedPeriods.push(firstPeriod);
      if (i === periods.length - 2) {
        collapsedPeriods.push(secondPeriod);
      }
      i = i + 1;
    }
    // all periods for the day are collapsed
    periods = collapsedPeriods;
  }
  return periods;
};

// parseDayparting is run when component mounts and is used to transform the dayparting string into dayparting intervals
export const parseDayparting = (dayparting: string) => {
  let daypartingObj;
  try {
    daypartingObj = JSON.parse(dayparting);
  } catch (e) {
    daypartingObj = {
      Enabled: false,
      Schedule: cloneDeep(scheduleStrings),
    };
  }
  const isDaypartingActive = daypartingObj.Enabled;
  const DaypartingIntervals = findDaypartingIntervals(cloneDeep(daypartingObj.Schedule));
  const enableViewerTimezone = daypartingObj.EnableViewerTimezone || false;
  return [isDaypartingActive, DaypartingIntervals, enableViewerTimezone];
};

export const findDaypartingIntervals = (schedule: ISchedule): IDaypartingInterval[] => {
  const DaypartingInterval: IDaypartingInterval = {
    ActiveDays: {
      Sunday: false,
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
    },
    StartTime: moment().startOf('day'),
    EndTime: moment().startOf('day'),
    Error: '',
  };
  const DaypartingIntervals = [];
  let collapsed = false;
  while (!collapsed) {
    collapsed = true;
    const newInterval = cloneDeep(DaypartingInterval);
    let checkPeriod;
    for (const key of Object.keys(schedule) as Day[]) {
      let day: Day;
      if (
        ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(key)
      ) {
        day = key;
      } else {
        return [] as IDaypartingInterval[];
      }
      for (let i = 0; i < schedule[day].length; i++) {
        const period = schedule[day][i];
        newInterval.ActiveDays[day] = true;
        newInterval.StartTime = newInterval.StartTime.add(
          parseInt(period[0].split(':')[0], 10),
          'hours',
        ).add(parseInt(period[0].split(':')[1], 10), 'minutes');
        newInterval.EndTime = newInterval.EndTime.add(
          parseInt(period[1].split(':')[0], 10),
          'hours',
        ).add(parseInt(period[1].split(':')[1], 10), 'minutes');
        checkPeriod = period;
        collapsed = false;
        schedule[day].splice(i, 1);
        break;
      }
      if (checkPeriod) {
        break;
      }
    }
    for (const day of Object.keys(schedule)) {
      for (let i = 0; i < schedule[day].length; i++) {
        const period = schedule[day][i];
        if (checkPeriod[0] === period[0] && checkPeriod[1] === period[1]) {
          schedule[day].splice(i, 1);
          newInterval.ActiveDays[day] = true;
        }
      }
    }
    if (!collapsed) {
      DaypartingIntervals.push(newInterval);
    }
  }
  return DaypartingIntervals;
};

export const CurrentlyWithinDayparting = (
  currentTime: moment.Moment,
  daypartingIsAllowed: EAllowStatus,
  daypartingIntervals: IDaypartingInterval[],
): Boolean => {
  for (let i = 0; i < daypartingIntervals.length; i++) {
    const currentInterval = daypartingIntervals[i];
    const currWkDay = MomentIntToWeekdayStr[currentTime.day()];
    const isBetween = currentTime.isBetween(currentInterval.StartTime, currentInterval.EndTime);
    if (
      currentInterval.ActiveDays[currWkDay] &&
      ((isBetween && daypartingIsAllowed === EAllowStatus.ALLOW) ||
        (!isBetween && daypartingIsAllowed === EAllowStatus.DENY))
    ) {
      return true;
    }
  }

  return false;
};

export const DaypartingSelector = (props: IProps) => {
  const {
    DaypartingIntervals,
    handleDateChange,
    handleDayClick,
    handleDeleteDaypartingPeriod,
    handleAddDaypartingPeriod,
    classes,
  } = props;
  const buttonSelected = { backgroundColor: '#4FC1B5', color: 'white' };
  const buttonNotSelected = { backgroundColor: '#F5F5F5', color: 'black' };
  return (
    <Grid container style={{ marginBottom: 15 }}>
      <Grid container spacing={6}>
        <Grid item>
          {DaypartingIntervals.map((interval, i) => {
            const { ActiveDays, StartTime, EndTime, Error } = interval;
            return (
              <Grid
                container
                key={`${i}-dayparting-interval`}
                direction='column'
                style={{
                  padding: 15,
                  marginBottom: 20,
                  backgroundColor: '#F5F5F5',
                  borderRadius: 3,
                }}
              >
                <Grid item>
                  <Grid container spacing={6} direction='row' justify='space-between'>
                    <Grid item>
                      <Grid container alignItems='flex-start' spacing={6} direction='row'>
                        <Grid item style={{ maxWidth: 200 }}>
                          <TextField
                            className={classes.textField}
                            id='time-picker-start'
                            type='time'
                            label='Start'
                            value={StartTime.format('HH:mm')}
                            onChange={(e) => handleDateChange(i, e.target.value, 'start')}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            style={{ marginTop: 0 }}
                          />
                        </Grid>
                        <Grid item style={{ maxWidth: 200 }}>
                          <TextField
                            className={classes.textField}
                            id='time-picker-end'
                            type='time'
                            label='End'
                            value={EndTime.format('HH:mm')}
                            onChange={(e) => handleDateChange(i, e.target.value, 'end')}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            style={{ marginTop: 0 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item style={{ paddingTop: '12px' }}>
                      <Typography
                        style={{ fontSize: 20, cursor: 'pointer' }}
                        onClick={() => handleDeleteDaypartingPeriod(i)}
                      >
                        x
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  {Error !== '' && <Typography style={{ fontSize: 10 }}>{Error}</Typography>}
                </Grid>
                <Grid item>
                  <Typography style={{ fontSize: 14, marginTop: 10, marginBottom: 5 }}>
                    Repeating On
                  </Typography>
                </Grid>
                <Grid item style={{ marginTop: 5, marginBottom: 5 }}>
                  <ButtonGroup>
                    <Button
                      style={ActiveDays.Sunday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Sunday', i)}
                    >
                      Sun
                    </Button>
                    <Button
                      style={ActiveDays.Monday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Monday', i)}
                    >
                      Mon
                    </Button>
                    <Button
                      style={ActiveDays.Tuesday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Tuesday', i)}
                    >
                      Tue
                    </Button>
                    <Button
                      style={ActiveDays.Wednesday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Wednesday', i)}
                    >
                      Wed
                    </Button>
                    <Button
                      style={ActiveDays.Thursday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Thursday', i)}
                    >
                      Thu
                    </Button>
                    <Button
                      style={ActiveDays.Friday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Friday', i)}
                    >
                      Fri
                    </Button>
                    <Button
                      style={ActiveDays.Saturday ? buttonSelected : buttonNotSelected}
                      onClick={() => handleDayClick('Saturday', i)}
                    >
                      Sat
                    </Button>
                  </ButtonGroup>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Typography
        onClick={handleAddDaypartingPeriod}
        className={classes.textCenteredSpan}
        style={{ fontSize: 14, color: '#5865E8', fontWeight: 500, cursor: 'pointer' }}
      >
        + ADD TIME PERIOD
      </Typography>
    </Grid>
  );
};
